import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useConstants } from '@/contexts/ConstantsContext';
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText, 
  Search, 
  Copy,
  Check,
  X,
  RefreshCw,
  BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scriptTypeColors = {
  'Opener': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Gatekeeper': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Voicemail': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  'Objection': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Follow-up': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'Closing': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const Scripts = () => {
  const { isOwner } = useAuth();
  const { script_types, industries } = useConstants();
  
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterNiche, setFilterNiche] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [deleteScriptId, setDeleteScriptId] = useState(null);
  const [viewingScript, setViewingScript] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    niche_or_industry: '',
    script_type: '',
    script_text: '',
    approved: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchScripts();
  }, [filterType, filterNiche]);

  const fetchScripts = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('script_type', filterType);
      if (filterNiche) params.append('niche_or_industry', filterNiche);
      
      const response = await axios.get(`${API}/scripts?${params.toString()}`);
      setScripts(response.data);
    } catch (error) {
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingScript(null);
    setFormData({
      title: '',
      niche_or_industry: '',
      script_type: 'Opener',
      script_text: '',
      approved: true,
    });
    setModalOpen(true);
  };

  const openEditModal = (script) => {
    setEditingScript(script);
    setFormData({
      title: script.title,
      niche_or_industry: script.niche_or_industry,
      script_type: script.script_type,
      script_text: script.script_text,
      approved: script.approved,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.niche_or_industry || !formData.script_type || !formData.script_text) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingScript) {
        await axios.put(`${API}/scripts/${editingScript.id}`, formData);
        toast.success('Script updated');
      } else {
        await axios.post(`${API}/scripts`, formData);
        toast.success('Script created');
      }
      setModalOpen(false);
      fetchScripts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/scripts/${deleteScriptId}`);
      toast.success('Script deleted');
      setDeleteScriptId(null);
      fetchScripts();
    } catch (error) {
      toast.error('Failed to delete script');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Script copied to clipboard');
  };

  const filteredScripts = scripts.filter(s => 
    !search || 
    s.title.toLowerCase().includes(search.toLowerCase()) ||
    s.niche_or_industry.toLowerCase().includes(search.toLowerCase()) ||
    s.script_text.toLowerCase().includes(search.toLowerCase())
  );

  // Group scripts by type
  const scriptsByType = filteredScripts.reduce((acc, script) => {
    if (!acc[script.script_type]) {
      acc[script.script_type] = [];
    }
    acc[script.script_type].push(script);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="scripts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-primary" />
            Scripts Library
          </h1>
          <p className="text-muted-foreground mt-1">Sales scripts for cold calling</p>
        </div>
        {isOwner && (
          <Button onClick={openCreateModal} data-testid="create-script-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Script
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search scripts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-secondary border-border"
            data-testid="search-scripts"
          />
        </div>
        
        <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 bg-secondary border-border">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {(script_types || []).map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterNiche || "all"} onValueChange={(v) => setFilterNiche(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 bg-secondary border-border">
            <SelectValue placeholder="All industries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All industries</SelectItem>
            {(industries || []).map(i => (
              <SelectItem key={i} value={i}>{i}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={fetchScripts} className="border-border">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredScripts.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BookOpen className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No scripts found</p>
            {isOwner && (
              <Button className="mt-4" onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                Create your first script
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(scriptsByType).map(([type, typeScripts]) => (
            <div key={type}>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className={scriptTypeColors[type]}>
                  {type}
                </Badge>
                <span className="text-sm text-muted-foreground">({typeScripts.length})</span>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {typeScripts.map(script => (
                  <Card 
                    key={script.id} 
                    className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer"
                    onClick={() => setViewingScript(script)}
                    data-testid={`script-card-${script.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{script.title}</CardTitle>
                          <CardDescription className="mt-1">{script.niche_or_industry}</CardDescription>
                        </div>
                        {!script.approved && (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                            Draft
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {script.script_text}
                      </p>
                      <p className="text-xs text-muted-foreground mt-3">
                        by {script.created_by_name} • {format(new Date(script.updated_at), 'MMM d, yyyy')}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Script Modal */}
      <Dialog open={!!viewingScript} onOpenChange={(open) => !open && setViewingScript(null)}>
        <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl pr-8">{viewingScript?.title}</DialogTitle>
          </DialogHeader>
          
          {viewingScript && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className={scriptTypeColors[viewingScript.script_type]}>
                  {viewingScript.script_type}
                </Badge>
                <Badge variant="outline" className="border-border">
                  {viewingScript.niche_or_industry}
                </Badge>
                {!viewingScript.approved && (
                  <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                    Draft
                  </Badge>
                )}
              </div>
              
              <ScrollArea className="flex-1 pr-4">
                <div className="bg-secondary/50 p-4 rounded-lg whitespace-pre-wrap text-sm">
                  {viewingScript.script_text}
                </div>
              </ScrollArea>
              
              <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                <p className="text-xs text-muted-foreground">
                  Created by {viewingScript.created_by_name} • Updated {format(new Date(viewingScript.updated_at), 'MMM d, yyyy')}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(viewingScript.script_text)}
                    className="border-border"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                  {isOwner && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setViewingScript(null); openEditModal(viewingScript); }}
                        className="border-border"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setViewingScript(null); setDeleteScriptId(viewingScript.id); }}
                        className="border-destructive text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Script Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[90vh] flex flex-col" data-testid="script-modal">
          <DialogHeader>
            <DialogTitle>{editingScript ? 'Edit Script' : 'Create Script'}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Opener - Healthcare"
                  className="bg-secondary border-border"
                  data-testid="script-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Niche/Industry *</Label>
                <Input
                  value={formData.niche_or_industry}
                  onChange={(e) => setFormData({ ...formData, niche_or_industry: e.target.value })}
                  placeholder="e.g., Healthcare, Osteopathy"
                  className="bg-secondary border-border"
                  data-testid="script-niche"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Script Type *</Label>
                <Select value={formData.script_type} onValueChange={(v) => setFormData({ ...formData, script_type: v })}>
                  <SelectTrigger className="bg-secondary border-border" data-testid="script-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(script_types || ['Opener', 'Gatekeeper', 'Voicemail', 'Objection', 'Follow-up', 'Closing']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg h-10">
                  <span className="text-sm">{formData.approved ? 'Approved' : 'Draft'}</span>
                  <Switch
                    checked={formData.approved}
                    onCheckedChange={(v) => setFormData({ ...formData, approved: v })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Script Text *</Label>
              <Textarea
                value={formData.script_text}
                onChange={(e) => setFormData({ ...formData, script_text: e.target.value })}
                placeholder="Enter your script here...

Example:
Hi, this is [Name] from Lumero. We help [industry] businesses automate their operations with AI...

Is [Decision Maker] available?"
                className="bg-secondary border-border resize-none min-h-[200px]"
                data-testid="script-text"
              />
            </div>
          </div>

          <div className="flex-shrink-0 pt-4 border-t border-border">
            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={saving}
              data-testid="save-script-btn"
            >
              {saving ? 'Saving...' : editingScript ? 'Update Script' : 'Create Script'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteScriptId} onOpenChange={(open) => !open && setDeleteScriptId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Script</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this script? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Scripts;
